import React, { useMemo, useState } from 'react';
import useHardwareTypes from '../../hooks/hardware/query/useHardwareTypes';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IAuth } from '../../ts/interface/admins.interface';
import {
  IAddSystemDispatchProps,
  IAddSystemProps,
} from '../../ts/interface/pages/system.interface';
import {
  BEACH_CHAIR,
  BIKE,
  KAYAK,
  ADAPTIVE_BIKE,
  LAWN_GAME,
  LIFE_JACKET,
  LOCKER,
  RACK,
  SCOOTER,
} from '../../constants';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupCheck,
  MovaticFieldGroupSelect,
  MovaticFieldGroupText,
} from '../../components/Redesing/movatic-field-group';
import MenuItem from '@mui/material/MenuItem';
import PageContainer from '../../components/Redesing/page-container';
import CardView from '../../components/Redesing/card-view';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import { Stack } from '@mui/system';
import { createSystem } from '../../api/system';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import { history } from '../../utils';
import countries from '../../../data/countries.json';
import CloseIcon from '@mui/icons-material/Close';
import BigButton from '../../components/Redesing/big-button';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { useUpdateEffect } from '../../hooks/use-update-effect';
import useSystems from '../../hooks/system/query/useSystems';
import useOrganization from '../../hooks/use-organization';
import useOrganizations from '../../hooks/system/query/useOrganizations';
import { Option } from '../../ts/interface/components/table.interface';

interface IAddSystemState {
  name: string;
  country: string;
  selectedMobilityType: string;
  demoSystem: boolean;
  adminUserEmail: string;
  adminUserFirstName: string;
  adminUserLastName: string;
  mobilityTypes: { name: string; id: string }[];
  nameValidationResult: null | string;
  nameValidationHelp: string;
  countryValidationResult: null | string;
  countryValidationHelp: string;

  selectedMobilityTypeValidationResult: null | string;
  selectedMobilityTypeValidationHelp: string;
  adminUserEmailValidationResult: null | string;
  adminUserEmailValidationHelp: string;
  adminUserFirstNameValidationResult: null | string;
  adminUserFirstNameValidationHelp: string;
  adminUserLastNameValidationResult: null | string;
  adminUserLastNameValidationHelp: string;
  organization: Option | null;
  organizationValidationResult: null | string;
  organizationValidationHelp: string;
}

interface ISystemItem {
  name: string;
  id: string;
  gid: string;
  country: string;
  partner: string;
  hardware_type_gid: string;
}

const Add = ({ systemLoaded, admin, auth, system }: IAddSystemProps) => {
  const initialState = {
    name: '',
    country: '',
    selectedMobilityType: '',
    demoSystem: true,
    adminUserEmail: '',
    adminUserFirstName: '',
    adminUserLastName: '',
    mobilityTypes: [],
    nameValidationResult: null,
    nameValidationHelp: '',
    countryValidationResult: null,
    countryValidationHelp: '',

    selectedMobilityTypeValidationResult: null,
    selectedMobilityTypeValidationHelp: '',
    adminUserEmailValidationResult: null,
    adminUserEmailValidationHelp: '',
    adminUserFirstNameValidationResult: null,
    adminUserFirstNameValidationHelp: '',
    adminUserLastNameValidationResult: null,
    adminUserLastNameValidationHelp: '',
    organization: null,
    organizationValidationResult: null,
    organizationValidationHelp: '',
  };
  const [
    {
      name,
      country,
      selectedMobilityType,
      demoSystem,
      adminUserEmail,
      adminUserFirstName,
      adminUserLastName,
      nameValidationResult,
      nameValidationHelp,
      countryValidationResult,
      countryValidationHelp,
      selectedMobilityTypeValidationResult,
      selectedMobilityTypeValidationHelp,
      adminUserEmailValidationResult,
      adminUserEmailValidationHelp,
      adminUserFirstNameValidationResult,
      adminUserFirstNameValidationHelp,
      adminUserLastNameValidationResult,
      adminUserLastNameValidationHelp,
      organization,
      organizationValidationResult,
      organizationValidationHelp,
    },
    setState,
  ] = useState<IAddSystemState>(initialState);

  const [openAsyncSelect, setOpenAsyncSelect] = useState(false);

  const organizationInfo = useOrganization();

  const { data: dataHardwareTypes, isLoading: isLoadingTypes } = useHardwareTypes();

  const {
    data: organizations,
    isLoading: isOrganizationsLoading,
    refetch: refetchOrganizations,
  } = useOrganizations();

  const mobilityTypeOptions = useMemo(() => {
    if (!dataHardwareTypes) return [];

    return [
      {
        value: dataHardwareTypes[BIKE].gid,
        name: 'Bike Share',
        old_id: dataHardwareTypes[BIKE].old_id,
      },
      {
        value: dataHardwareTypes[RACK].gid,
        name: 'Bike Room',
        old_id: dataHardwareTypes[RACK].old_id,
      },
      {
        value: dataHardwareTypes[LOCKER].gid,
        name: 'Bike Locker',
        old_id: dataHardwareTypes[LOCKER].old_id,
      },
      {
        value: dataHardwareTypes[SCOOTER].gid,
        name: 'Scooter',
        old_id: dataHardwareTypes[SCOOTER].old_id,
      },
      {
        value: dataHardwareTypes[KAYAK].gid,
        name: 'Kayak',
        old_id: dataHardwareTypes[KAYAK].old_id,
      },
      {
        value: dataHardwareTypes[BEACH_CHAIR].gid,
        name: 'Beach Chairs',
        old_id: dataHardwareTypes[BEACH_CHAIR].old_id,
      },
      {
        value: dataHardwareTypes[LIFE_JACKET].gid,
        name: 'Life Jacket',
        old_id: dataHardwareTypes[LIFE_JACKET].old_id,
      },
      {
        value: dataHardwareTypes[LAWN_GAME].gid,
        name: 'Lawn Game',
        old_id: dataHardwareTypes[LAWN_GAME].old_id,
      },
      {
        value: dataHardwareTypes[ADAPTIVE_BIKE].gid,
        name: 'Recumbent Bike',
        old_id: dataHardwareTypes[ADAPTIVE_BIKE].old_id,
      },
    ];
  }, [dataHardwareTypes]);

  const [systemSearch, setSystemSearch] = useState('');
  const systemDebouncedSearchTerm = useSearchDebounce(systemSearch);
  const [systemItems, setSystemItems] = useState<ISystemItem[]>([]);
  const [openSystemSelect, setOpenSystemSelect] = useState(false);
  const [isSystemLoading, setIsSystemLoading] = useState(false);
  const [selectedSystem, setSelectedSystemId] = useState<ISystemItem | null>(null);

  const [useCopySystemSetting, setUseCopySystemSetting] = useState(false);

  const { refetch } = useSystems(
    {
      enabled: false,
    },
    {
      search: systemDebouncedSearchTerm,
    }
  );

  useUpdateEffect(() => {
    const fetchSystems = async () => {
      setIsSystemLoading(true);
      const res = await refetch();
      if (res.status === 'success') {
        const { id, name, gid } = system;
        const currentSystem = {
          id,
          name,
          gid,
          country: system.country,
          partner: system.partner,
          hardware_type_gid: system.hardware_type_gid,
        };
        const { data } = res;
        const systems = data.systems;

        const otherAccessibleSystems = systems
          .filter((system: ISystem) => currentSystem.id !== system.id)
          .map((system: ISystem) => ({
            name: system.name,
            id: system.id,
            gid: system.gid,
            country: system.country,
            partner: system.partner,
            hardware_type_gid: system.hardware_type_gid,
          }));
        setSystemItems([currentSystem, ...otherAccessibleSystems]);
      }
      setIsSystemLoading(false);
    };

    if (openSystemSelect) {
      fetchSystems();
    }

    if (openAsyncSelect) {
      refetchOrganizations();
    }
  }, [systemDebouncedSearchTerm, openSystemSelect, system, openAsyncSelect]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventType = event.target.type;
    const eventName = event.target.name;
    let value = eventType === 'checkbox' ? event.target.checked : event.target.value;
    if (eventType === 'text' || eventType === 'select-one') {
      setState((prevState) => ({
        ...prevState,
        [eventName.concat('ValidationResult')]: null,
        [eventName.concat('ValidationHelp')]: '',
      }));
    }
    setState((prevState) => ({
      ...prevState,
      [eventName]: value,
    }));
  };
  const checkValidation = () => {
    let valid = true;
    if (!name) {
      setState((prevState) => ({
        ...prevState,
        nameValidationResult: 'error',
        nameValidationHelp: 'Please add a system name.',
      }));
      valid = false;
    }
    if (!country) {
      setState((prevState) => ({
        ...prevState,
        countryValidationResult: 'error',
        countryValidationHelp: 'Please select a country.',
      }));
      valid = false;
    }

    if (admin && !organization) {
      setState((prevState) => ({
        ...prevState,
        organizationValidationResult: 'error',
        organizationValidationHelp: 'Please select an organization.',
      }));
      valid = false;
    }

    if (selectedMobilityType === '') {
      setState((prevState) => ({
        ...prevState,
        selectedMobilityTypeValidationResult: 'error',
        selectedMobilityTypeValidationHelp: 'Please select a mobility type.',
      }));
      valid = false;
    }
    if (!adminUserEmail) {
      setState((prevState) => ({
        ...prevState,
        adminUserEmailValidationResult: 'error',
        adminUserEmailValidationHelp: 'Please add admin user email.',
      }));
      valid = false;
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,4})+$/.test(adminUserEmail)) {
      setState((prevState) => ({
        ...prevState,
        adminUserEmailValidationResult: 'error',
        adminUserEmailValidationHelp: 'Please enter a valid email address.',
      }));
      return;
    }
    if (!adminUserFirstName) {
      setState((prevState) => ({
        ...prevState,
        adminUserFirstNameValidationResult: 'error',
        adminUserFirstNameValidationHelp: 'Please add admin user first name.',
      }));
      valid = false;
    }
    if (!adminUserLastName) {
      setState((prevState) => ({
        ...prevState,
        adminUserLastNameValidationResult: 'error',
        adminUserLastNameValidationHelp: 'Please add admin user last name.',
      }));
      valid = false;
    }
    return valid;
  };
  const submit = () => {
    if (checkValidation()) {
      const system = {
        name: name.trim(),
        country,
        organization_id: organization?.value,
        rentable_hardware_type_id: selectedMobilityType,
        demoSystem,
        adminUser: {
          email: adminUserEmail,
          firstName: adminUserFirstName,
          lastName: adminUserLastName,
        },
        copy_system_id: selectedSystem?.gid || null,
      };
      CustomAsyncToast({
        promise: () => createSystem(system),
        loadingMessage: 'Creating System...',
        successMessage: () => 'System Created Successfully',
        errorMessage: 'Error Creating System.',
      }).then((r) => {
        if (r) {
          history.push(`/dashboard`);
        }
      });
    }
  };

  const isLoading = !organizationInfo?.id && !systemLoaded;

  const organizationOptions =
    organizations?.data?.map((org: { id: string; name: string }) => ({
      value: org.id,
      label: org.name,
    })) || [];

  return (
    <PageContainer isLoading={isLoading || isLoadingTypes}>
      <CardView
        content={
          <Stack px={2} pb={2}>
            {/* SYSTEM NAME */}
            <MovaticFieldGroupText
              id="name"
              name="name"
              label="System Name"
              description="Required"
              hasError={nameValidationResult === 'error'}
              errorMessage={nameValidationHelp}
              value={name}
              onChange={handleChange}
            />

            <MovaticFieldGroupCheck
              id="check-copy-system"
              label={'Enable copy configuration'}
              description={`Enable this option to copy the settings from an existing system configuration.`}
              name="check-copy-system"
              onChange={() => {
                setUseCopySystemSetting(!useCopySystemSetting);
                setSelectedSystemId(null);
                setState((prevState) => ({
                  ...prevState,
                  selectedMobilityType: '',
                  partner: '',
                  country: '',
                }));
              }}
              checked={useCopySystemSetting}
            />

            {useCopySystemSetting && (
              <MovaticFieldGroupAsyncSelect
                id="copy-system-name"
                isLoading={isSystemLoading}
                label="Copy configuration from"
                description={`Choose a system to copy settings from. If the system you need isn't listed, try searching by name.`}
                name={'copy-system-name'}
                value={selectedSystem}
                showClearButton
                open={openSystemSelect}
                isOptionEqualToValue={(option: any, value: any) =>
                  value !== null && option.id === value.id
                }
                getOptionText={(option: any) => option.name}
                options={systemItems}
                handleSearch={(search) => setSystemSearch(search.target.value)}
                onOpen={() => setOpenSystemSelect(true)}
                onClose={() => setOpenSystemSelect(false)}
                onChange={(event: any, item: ISystemItem) => {
                  setSelectedSystemId(item);
                  if (item) {
                    setState((prevState) => ({
                      ...prevState,
                      selectedMobilityType: item.hardware_type_gid,
                      partner: String(item.partner),
                      country: item.country,
                    }));
                  } else {
                    setState((prevState) => ({
                      ...prevState,
                      selectedMobilityType: '',
                      partner: '',
                      country: '',
                    }));
                  }
                }}
              />
            )}

            {/* COUNTRY */}
            <MovaticFieldGroupSelect
              label="Country"
              description={"Required. Don't see your country? Contact support@movatic.co"}
              name={'country'}
              optionNodes={countries.map((country) => (
                <MenuItem key={country['alpha-2']} value={country['alpha-2']}>
                  {country.label}
                </MenuItem>
              ))}
              value={country || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setState((prevState) => ({
                  ...prevState,
                  countryValidationResult: null,
                  countryValidationHelp: '',
                  country: e.target.value,
                }));
              }}
              hasError={countryValidationResult === 'error'}
              errorMessage={countryValidationHelp}
              id={'country'}
              disabled={
                useCopySystemSetting && Boolean(selectedSystem) && Boolean(country)
              }
            />

            {/* ORGANIZATIONS (PARTNER ONLY) */}
            {admin ? (
              <MovaticFieldGroupAsyncSelect
                isLoading={isOrganizationsLoading}
                id={'organization'}
                label="Organization"
                name={'organization'}
                description="Required"
                options={organizationOptions}
                value={organization}
                showClearButton
                onChange={(event: any, newValue: Option) => {
                  console.log({ newValue });
                  setState((prevState) => ({
                    ...prevState,
                    organization: newValue || null,
                  }));
                }}
                errorMessage={organizationValidationHelp}
                hasError={organizationValidationResult === 'error'}
                open={openAsyncSelect}
                onOpen={() => {
                  setOpenAsyncSelect(!openAsyncSelect);
                }}
                onClose={() => {
                  setOpenAsyncSelect(!openAsyncSelect);
                }}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value.value
                }
                getOptionText={(option: any) => option.label}
                handleSearch={() => {}}
              />
            ) : null}

            {/* MOBILITY TYPE */}
            <MovaticFieldGroupSelect
              label="Mobility Type"
              description="Required"
              name={'selectedMobilityType'}
              optionNodes={mobilityTypeOptions.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
              value={selectedMobilityType || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setState((prevState) => ({
                  ...prevState,
                  selectedMobilityTypeValidationResult: null,
                  selectedMobilityTypeValidationHelp: '',
                  selectedMobilityType: e.target.value,
                }));
              }}
              hasError={selectedMobilityTypeValidationResult === 'error'}
              errorMessage={selectedMobilityTypeValidationHelp}
              id={'selectedMobilityType'}
              disabled={
                useCopySystemSetting &&
                Boolean(selectedSystem) &&
                Boolean(selectedMobilityType)
              }
            />

            {/* DEMO CHECKBOX */}
            <MovaticFieldGroupCheck
              id="demoSystem"
              label={'Demo System'}
              description="Is this system for demonstration purposes only?"
              name="demoSystem"
              onChange={handleChange}
              checked={demoSystem}
            />

            {/* ADMIN FIRST NAME */}
            <MovaticFieldGroupText
              id="adminFirstName"
              name="adminFirstName"
              label="Admin User First Name"
              description="First Name of the first user of the system."
              hasError={adminUserFirstNameValidationResult === 'error'}
              errorMessage={adminUserFirstNameValidationHelp}
              value={adminUserFirstName}
              onChange={(event) => {
                setState((prevState) => ({
                  ...prevState,
                  adminUserFirstNameValidationResult: null,
                  adminUserFirstNameValidationHelp: '',
                  adminUserFirstName: event.target.value,
                }));
              }}
            />

            {/* ADMIN LAST NAME */}
            <MovaticFieldGroupText
              id="adminLastName"
              name="adminLastName"
              label="Admin User Last Name"
              description="Last Name of the first user of the system."
              hasError={adminUserLastNameValidationResult === 'error'}
              errorMessage={adminUserLastNameValidationHelp}
              value={adminUserLastName}
              onChange={(event) => {
                setState((prevState) => ({
                  ...prevState,
                  adminUserLastNameValidationResult: null,
                  adminUserLastNameValidationHelp: '',
                  adminUserLastName: event.target.value,
                }));
              }}
            />

            {/* ADMIN EMAIL */}
            <MovaticFieldGroupText
              id="adminUserEmail"
              name="adminUserEmail"
              label="Admin User Email"
              description="Email of the first user of the system."
              hasError={adminUserEmailValidationResult === 'error'}
              errorMessage={adminUserEmailValidationHelp}
              value={adminUserEmail}
              onChange={(event) => {
                setState((prevState) => ({
                  ...prevState,
                  adminUserEmailValidationResult: null,
                  adminUserEmailValidationHelp: '',
                  adminUserEmail: event.target.value,
                }));
              }}
            />
            <BigButton description="Create Your System" onClick={submit} />
          </Stack>
        }
        title={'Add System'}
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                startIcon: <CloseIcon />,
                onClick: () => history.push(`/dashboard`),
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean };
    admin: { admin: IAuth };
  }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    systemId: state.system.current.id,
    auth: state.admin.admin,
    admin: state.admin.admin.admin.movaticAccess,
  }),
  (dispatch: Function) => ({
    createSystem(system: IAddSystemDispatchProps) {
      dispatch(createSystem(system));
    },
  })
)(Add);
